// src/utils/letterTemplates.ts
// ──────────────────────────────────────────────────────────────
// Client-side letter template engine.
// Assembles the final letter by combining:
//   1. Claude-generated clinical argument (from server, no PHI)
//   2. Patient/provider details (from local case data, PHI)
//
// The assembled letter exists ONLY in the browser and is never
// sent to any server or persisted in any database.
// ──────────────────────────────────────────────────────────────

export interface LetterSlots {
  // Provider info (from physician_profiles)
  practice_name?: string;
  practice_address?: string;
  practice_phone?: string;
  practice_fax?: string;
  physician_name?: string;
  physician_npi?: string;
  physician_specialty?: string;

  // Patient info (from case record, entered by physician)
  patient_name?: string;
  patient_dob?: string;
  member_id?: string;

  // Case info (from case record)
  insurance_company?: string;
  plan_type?: string;
  date_of_service?: string;
  diagnosis_display?: string;
  procedure_display?: string;
  denial_reason?: string;
  denial_date?: string;

  // Claude-generated content
  clinical_justification?: string;
  criteria_citations?: string[];
  literature_references?: string[];
  risks_of_denial?: string[];
  recommended_additions?: string[];
  regulatory_leverage?: string[];

  // System
  date_generated?: string;
}

export interface LetterTemplate {
  id: string;
  name: string;
  description: string;
  requestTypes: string[]; // Which request types this template supports
  render: (slots: LetterSlots) => string;
}

// ── Helpers ──────────────────────────────────────────────────

function formatDate(date?: string): string {
  if (!date) return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function slot(value?: string, fallback = "[____________]"): string {
  return value?.trim() || fallback;
}

function formatCitations(citations?: string[]): string {
  if (!citations?.length) return "";
  return citations.map((c, i) => `${i + 1}. ${c}`).join("\n");
}

function formatReferences(refs?: string[]): string {
  if (!refs?.length) return "";
  return refs.map((r, i) => `${i + 1}. ${r}`).join("\n");
}

function formatRisks(risks?: string[]): string {
  if (!risks?.length) return "";
  return risks.map((r, i) => `${i + 1}. ${r}`).join("\n");
}

function formatRegulatoryLeverage(items?: string[]): string {
  if (!items?.length) return "";
  return items.map((r, i) => `${i + 1}. ${r}`).join("\n");
}

// ── Standard Prior Auth Template ─────────────────────────────

const standardV1: LetterTemplate = {
  id: "standard_v1",
  name: "Standard Prior Authorization",
  description: "Initial prior authorization request with medical necessity argument",
  requestTypes: ["initial", "reauthorization"],
  render: (s: LetterSlots) => `${slot(s.practice_name, "[Practice Name]")}
${slot(s.practice_address, "[Practice Address]")}
${s.practice_phone ? `Phone: ${s.practice_phone}` : ""}${s.practice_fax ? `  |  Fax: ${s.practice_fax}` : ""}

${formatDate(s.date_generated)}

Medical Director
Prior Authorization Department
${slot(s.insurance_company, "[Insurance Company]")}

RE: Prior Authorization Request
Patient: ${slot(s.patient_name, "[Patient Name]")}
Date of Birth: ${slot(s.patient_dob, "[DOB]")}
Member ID: ${slot(s.member_id, "[Member ID]")}
${s.plan_type ? `Plan: ${s.plan_type}` : ""}

Dear Medical Director,

I am writing to request prior authorization for the following service for the above-referenced patient:

Service Requested: ${slot(s.procedure_display, "[Procedure/Service]")}
${s.date_of_service ? `Date of Service: ${s.date_of_service}` : ""}
Diagnosis: ${slot(s.diagnosis_display, "[Diagnosis]")}

MEDICAL NECESSITY

${slot(s.clinical_justification, "[Clinical justification will be generated — click 'Generate Letter' to create]")}

CRITERIA MET
${s.criteria_citations?.length ? `\n${formatCitations(s.criteria_citations)}` : "\n[Criteria citations will be generated]"}

SUPPORTING EVIDENCE
${s.literature_references?.length ? `\n${formatReferences(s.literature_references)}` : "\n[Literature references will be generated]"}

RISKS OF DENIAL
${s.risks_of_denial?.length ? `\n${formatRisks(s.risks_of_denial)}` : "\n[Risk assessment will be generated]"}

I respectfully request that this authorization be approved based on the clinical evidence presented above. I am available for peer-to-peer review at your earliest convenience should you wish to discuss this case.

Sincerely,

${slot(s.physician_name, "[Physician Name]")}${s.physician_specialty ? `, ${s.physician_specialty}` : ""}
NPI: ${slot(s.physician_npi, "[NPI]")}
${slot(s.practice_name, "[Practice Name]")}`,
};

// ── Appeal Letter Template ───────────────────────────────────

const appealV1: LetterTemplate = {
  id: "appeal_v1",
  name: "Appeal Letter",
  description: "Post-denial appeal with point-by-point rebuttal and regulatory leverage",
  requestTypes: ["appeal"],
  render: (s: LetterSlots) => {
    // Build the regulatory leverage section only if populated
    const regulatorySection = s.regulatory_leverage?.length
      ? `\nAPPLICABLE REGULATORY PROTECTIONS\n\n${formatRegulatoryLeverage(s.regulatory_leverage)}\n`
      : "";

    return `${slot(s.practice_name, "[Practice Name]")}
${slot(s.practice_address, "[Practice Address]")}
${s.practice_phone ? `Phone: ${s.practice_phone}` : ""}${s.practice_fax ? `  |  Fax: ${s.practice_fax}` : ""}

${formatDate(s.date_generated)}

Medical Director
Appeals Department
${slot(s.insurance_company, "[Insurance Company]")}

RE: FORMAL APPEAL — Prior Authorization Denial
Patient: ${slot(s.patient_name, "[Patient Name]")}
Date of Birth: ${slot(s.patient_dob, "[DOB]")}
Member ID: ${slot(s.member_id, "[Member ID]")}
${s.plan_type ? `Plan: ${s.plan_type}` : ""}
${s.denial_date ? `Date of Denial: ${s.denial_date}` : ""}

Dear Medical Director,

I am writing to formally appeal the denial of prior authorization for the following service:

Service Requested: ${slot(s.procedure_display, "[Procedure/Service]")}
Diagnosis: ${slot(s.diagnosis_display, "[Diagnosis]")}
${s.denial_reason ? `\nReason for Denial: ${s.denial_reason}` : ""}

REBUTTAL OF DENIAL

${slot(s.clinical_justification, "[Clinical rebuttal will be generated — click 'Generate Letter' to create]")}

SPECIFIC CRITERIA MET
${s.criteria_citations?.length ? `\n${formatCitations(s.criteria_citations)}` : "\n[Criteria citations will be generated]"}

SUPPORTING CLINICAL EVIDENCE
${s.literature_references?.length ? `\n${formatReferences(s.literature_references)}` : "\n[Literature references will be generated]"}

RISKS OF CONTINUED DENIAL
${s.risks_of_denial?.length ? `\n${formatRisks(s.risks_of_denial)}` : "\n[Risk assessment will be generated]"}
${regulatorySection}
Based on the clinical evidence presented, the denial of this service is inconsistent with established medical necessity criteria and current evidence-based practice guidelines. I respectfully request that this denial be overturned and authorization be granted.

If this appeal is not approved, I request that the denial be reviewed by a board-certified physician in the same or similar specialty as the treating physician, as required by applicable regulations. I am available for peer-to-peer discussion at your earliest convenience.

Sincerely,

${slot(s.physician_name, "[Physician Name]")}${s.physician_specialty ? `, ${s.physician_specialty}` : ""}
NPI: ${slot(s.physician_npi, "[NPI]")}
${slot(s.practice_name, "[Practice Name]")}`;
  },
};

// ── Urgent/Expedited Template ────────────────────────────────

const urgentV1: LetterTemplate = {
  id: "urgent_v1",
  name: "Urgent/Expedited Request",
  description: "Time-sensitive authorization emphasizing clinical urgency",
  requestTypes: ["initial", "reauthorization", "appeal"],
  render: (s: LetterSlots) => `${slot(s.practice_name, "[Practice Name]")}
${slot(s.practice_address, "[Practice Address]")}
${s.practice_phone ? `Phone: ${s.practice_phone}` : ""}${s.practice_fax ? `  |  Fax: ${s.practice_fax}` : ""}

${formatDate(s.date_generated)}

*** URGENT / EXPEDITED REVIEW REQUESTED ***

Medical Director
Prior Authorization Department
${slot(s.insurance_company, "[Insurance Company]")}

RE: URGENT Prior Authorization Request
Patient: ${slot(s.patient_name, "[Patient Name]")}
Date of Birth: ${slot(s.patient_dob, "[DOB]")}
Member ID: ${slot(s.member_id, "[Member ID]")}

Dear Medical Director,

I am requesting EXPEDITED review of this prior authorization. A delay in approval poses a risk of serious harm to the patient's health, including potential for irreversible deterioration.

Service Requested: ${slot(s.procedure_display, "[Procedure/Service]")}
Diagnosis: ${slot(s.diagnosis_display, "[Diagnosis]")}

CLINICAL URGENCY AND MEDICAL NECESSITY

${slot(s.clinical_justification, "[Clinical argument will be generated]")}

CRITERIA MET
${s.criteria_citations?.length ? `\n${formatCitations(s.criteria_citations)}` : "\n[Criteria citations will be generated]"}

RISKS OF DELAY OR DENIAL
${s.risks_of_denial?.length ? `\n${formatRisks(s.risks_of_denial)}` : ""}

Per federal and state regulations, this request qualifies for expedited review (72-hour turnaround). Delay in this service could result in significant adverse clinical outcomes.

Please contact me immediately if additional information is needed.

Sincerely,

${slot(s.physician_name, "[Physician Name]")}${s.physician_specialty ? `, ${s.physician_specialty}` : ""}
NPI: ${slot(s.physician_npi, "[NPI]")}
${slot(s.practice_name, "[Practice Name]")}`,
};

// ── Template Registry ────────────────────────────────────────

export const LETTER_TEMPLATES: Record<string, LetterTemplate> = {
  standard_v1: standardV1,
  appeal_v1: appealV1,
  urgent_v1: urgentV1,
};

export function getTemplate(id: string): LetterTemplate {
  return LETTER_TEMPLATES[id] || standardV1;
}

export function getTemplatesForRequestType(requestType: string): LetterTemplate[] {
  return Object.values(LETTER_TEMPLATES).filter((t) =>
    t.requestTypes.includes(requestType)
  );
}

/**
 * Assemble a complete letter from template + slots.
 * This runs ONLY in the browser. The returned string contains PHI
 * and must never be sent to any server.
 */
export function assembleLetter(templateId: string, slots: LetterSlots): string {
  const template = getTemplate(templateId);
  return template.render(slots);
}